/**
 * Content Stage — Stage 3: Content quality validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.4
 *
 * Validates:
 * - Explanation quality (not generic, has didactic depth)
 * - Distractor explanations (present, not generic, reference misconceptions)
 * - Forbidden vocabulary per tier
 * - Question text length per tier
 * - Module-specific rules (from governance validator)
 * - Notation requirements (LaTeX, comma decimals, units)
 *
 * Split into focused sub-modules under contentValidators/:
 * - textQualityChecks.ts — forbidden vocab, jargon, sentence length, text length
 * - explanationChecks.ts — explanation quality, didactic depth, rule references
 * - distractorChecks.ts — distractor quality, D-QUAL heuristics
 * - notationChecks.ts — LaTeX, comma decimals, euro, superscript
 * - assessmentChecks.ts — CITO params, cognitive level, plus-level enforcement
 */

import type { ValidationIssue } from '../issueContract';
import type { Question } from '../../../content';
import { validateTextQuality } from './contentValidators/textQualityChecks';
import { validateExplanationQuality } from './contentValidators/explanationChecks';
import { validateDistractorQuality } from './contentValidators/distractorChecks';
import { validateNotation } from './contentValidators/notationChecks';
import { validateAssessmentRequirements } from './contentValidators/assessmentChecks';

/**
 * Run content quality validation on a question.
 * Composes all content sub-validators into a single issue list.
 */
export function runContentStage(question: Question): ValidationIssue[] {
  return [...validateTextQuality(question), ...validateExplanationQuality(question), ...validateDistractorQuality(question), ...validateNotation(question), ...validateAssessmentRequirements(question)];
}